<template lang="pug">
.gift-card-preview(
  :class="{'is-rounded': actualStylePreset.cardStyle === 'rounded', 'has-cutout': actualStylePreset.showCutOut }" 
  :style="cardStyle"
  @click="$emit('click')"
)
  .card-content.px-5.pt-5.pb-6(
    :style="textStyle"
  )
    .logo(v-if="actualStylePreset.showLogo")
      img(:src="company.logo" alt="Logo" :class="{ 'is-white': actualStylePreset.logoColor === 'white', 'is-black': actualStylePreset.logoColor === 'black' }")
    .card-name
      p.heading.is-size-6 {{company.name}}
      h3.title.is-3(:style="textStyle") {{ value.title || 'Gift Card' }}
    .card-message(v-if="value.message")
      p.message-text {{ value.message }}
    .card-value
      p.heading {{$t('vouchers.remaining_heading')}}
      h2.title(:style="textStyle")
        currency-view(:value="value.remaining || 0")
        p.heading.is-size-6 
          | /
          currency-view(:value="value.value")
    .card-code
      .code {{ value.code }}
      p.help(v-if="scope === 'business'") {{$t(`sale.invoice_number`, {invoice: value.invoice})}}
    b-collapse(animation="slide" v-if="value.services.length" :open="false")
      template(#trigger="props")
        a.has-text-underline(:style="textStyle") {{$tc('vouchers.number_of_services',value.services.length)}}
      .list-item(v-for="{service, option} in value.services")
        p 
          service-name-view(:value="{service, option}")
    .card-validity
      span(v-if="new Date(value.expire) > new Date()")
        span(v-t="'misc.valid_til'")  
        |  
        date-view(:value="value.expire" format="D MMM YYYY")  
      template(v-else)
        span.tag.is-danger
          span(v-t="'validity.expired'") 
      //| Valid for {{ value.validity }} {{ $t(`misc.${value.validityUnit}`) }}
    
    .card-notes(v-if="value.terms")
      p {{ value.terms }}
    .card-notes(v-if="value.notes")
      p {{ value.notes }}
    .watermark powered by emly
</template>

<script>
import {mapState} from 'vuex'

export default {
  name: 'GiftCardPreview',
  props: {
    value: {type: Object, required: true},
    scope: {type: String, default: 'business'},
    stylePreset: {type: Object, default: undefined}
  },
  computed: {
    ...mapState('company', ['company']),
    actualStylePreset() {
      return this.stylePreset || this.company.vouchers.stylePreset
    },
    cardStyle() {
      return {
        background: `linear-gradient(to right, ${this.actualStylePreset.gradientStart}, ${this.actualStylePreset.gradientEnd})`,
        fontFamily: this.actualStylePreset.fontFamily
      }
    },
    textStyle() {
      return {
        color: this.actualStylePreset.fontColor
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.gift-card-preview {
  width: 100%;
  margin: 0 auto;
  max-width: 396px;

  border-radius: 8px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &.is-rounded {
    border-radius: 24px;

    &.has-cutout {
      &:before {
        border-radius: 24px;
      }
      &:after {
        border-radius: 24px;
      }
    }
  }

  &.has-cutout {
    position: relative;

    &:after {
      content: '';
      position: absolute;
      width: 24px;
      height: 48px;
      background: white;
      top: 50%;
      transform: translateY(-50%);
      z-index: 1;
      border-radius: 0 24px 24px 0;
      left: -12px;
      box-shadow: inset 2px 0 4px rgba(0, 0, 0, 0.1);
    }

    &:before {
      content: '';
      position: absolute;
      width: 24px;
      height: 48px;
      background: white;
      top: 50%;
      transform: translateY(-50%);
      z-index: 1;
      border-radius: 24px 0 0 24px;
      right: -12px;
      box-shadow: inset -2px 0 4px rgba(0, 0, 0, 0.1);
    }

    .card-content {
      margin: 0 16px;
    }
  }

  .card-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    color: white;
    position: relative;
  }

  .watermark {
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.7rem;
    opacity: 0.5;
    text-transform: uppercase;
    letter-spacing: 1px;
    white-space: nowrap;
  }

  .logo {
    text-align: right;
    img {
      max-height: 36px;
      opacity: 1;

      &.is-white {
        filter: brightness(0) invert(1);
      }

      &.is-black {
        filter: brightness(0);
      }
    }
  }

  .card-name {
    margin-top: 1rem;
    .title {
      color: white;
      font-weight: 600;
    }
  }

  .card-value {
    margin: 1rem 0;
    .title {
      color: white;
      font-size: 2.25rem;
    }
  }

  .card-code {
    background: rgba(255, 255, 255, 0.1);
    padding: 0.5rem;
    border-radius: 4px;
    font-family: monospace;
    margin: 0.5rem 0;

    .code-label {
      font-size: 0.8rem;
      opacity: 0.8;
      margin-bottom: 0.25rem;
    }

    .code {
      font-size: 1.2rem;
      letter-spacing: 2px;
    }
  }

  .card-validity {
    font-size: 1rem;
    opacity: 0.9;
  }

  .card-message {
    margin: 1rem 0;
    .message-text {
      font-size: 0.9rem;
      line-height: 1.4;
      font-style: italic;
      opacity: 0.9;
    }
  }

  .card-notes {
    font-size: 0.875rem;
    opacity: 0.8;
    margin-top: 0.5rem;
  }
}
</style>
